import React, { useState } from 'react'
import { useStore } from 'effector-react'
import {
  Badge,
  Button,
  Collapse,
  DatePicker,
  Select,
  Space,
  Spin,
  Table,
  Typography,
} from 'antd'
import { WarningOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import dayjs, { Dayjs } from 'dayjs'
import { $rdcCheckData, $rdcCheckLoading, loadRdcCheck } from './model'
import type { CurrencyData, TableRow } from './types'

const { Text } = Typography

function buildRows(currencies: CurrencyData[], currencyFilter: string[]): TableRow[] {
  const rows: TableRow[] = []

  const filtered = currencyFilter.length
    ? currencies.filter((c) => currencyFilter.includes(c.currency))
    : currencies

  filtered.forEach((cur) => {
    const { currency, deposit, withdrawal, inout: inoutData, tradingResult, balanceFix } = cur

    const entries: { statya: string; type: string; db: number; rdc: number; diff: number; diffUsd: number }[] = [
      { statya: 'Deposit', type: 'Account', ...deposit.account },
      { statya: 'Deposit', type: 'Wallet', ...deposit.wallet },
      { statya: 'Withdrawal', type: 'Account', ...withdrawal.account },
      { statya: 'Withdrawal', type: 'Wallet', ...withdrawal.wallet },
      { statya: 'Inout', type: 'Account', ...inoutData.account },
      { statya: 'Inout', type: 'Wallet', ...inoutData.wallet },
      { statya: 'Trading result', type: 'Account', ...tradingResult.account },
      { statya: 'Balance fix', type: 'Account', ...balanceFix.account },
    ]

    const twoRowStatyas = new Set(['Deposit', 'Withdrawal', 'Inout'])

    entries.forEach((entry, idx) => {
      rows.push({
        key: `${currency}-${entry.statya}-${entry.type}`,
        currency,
        ...entry,
        currencyRowSpan: idx === 0 ? 8 : 0,
        statyaRowSpan:
          twoRowStatyas.has(entry.statya) && entry.type === 'Account'
            ? 2
            : twoRowStatyas.has(entry.statya)
            ? 0
            : 1,
      })
    })
  })

  return rows
}

const columns: ColumnsType<TableRow> = [
  {
    title: 'Валюта',
    dataIndex: 'currency',
    width: 80,
    onCell: (row) => ({ rowSpan: row.currencyRowSpan }),
  },
  {
    title: 'Статья',
    dataIndex: 'statya',
    width: 140,
    onCell: (row) => ({ rowSpan: row.statyaRowSpan }),
  },
  {
    title: 'Тип',
    dataIndex: 'type',
    width: 100,
  },
  {
    title: 'БД',
    dataIndex: 'db',
    align: 'right',
    render: (v: number) => v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
  },
  {
    title: 'RDC',
    dataIndex: 'rdc',
    align: 'right',
    render: (v: number) => v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
  },
  {
    title: 'Разница',
    dataIndex: 'diff',
    align: 'right',
    render: (v: number) => (
      <Text type={v !== 0 ? 'danger' : undefined} strong={v !== 0}>
        {v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </Text>
    ),
  },
  {
    title: 'Разница USD',
    dataIndex: 'diffUsd',
    align: 'right',
    render: (v: number) => (
      <Text type={v !== 0 ? 'danger' : undefined} strong={v !== 0}>
        {v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </Text>
    ),
  },
]

export const RdcCheck: React.FC = () => {
  const data = useStore($rdcCheckData)
  const loading = useStore($rdcCheckLoading)

  const [dateFrom, setDateFrom] = useState<Dayjs | null>(dayjs().subtract(4, 'day'))
  const [dateTo, setDateTo] = useState<Dayjs | null>(dayjs())
  const [currencyFilter, setCurrencyFilter] = useState<string[]>([])

  const allCurrencies = Array.from(
    new Set(data.flatMap((d) => d.currencies.map((c) => c.currency))),
  ).sort()

  const hasDiff = (currencies: CurrencyData[]) =>
    currencies.some(
      (c) =>
        c.deposit.account.diff !== 0 ||
        c.deposit.wallet.diff !== 0 ||
        c.withdrawal.account.diff !== 0 ||
        c.withdrawal.wallet.diff !== 0 ||
        c.inout.account.diff !== 0 ||
        c.inout.wallet.diff !== 0 ||
        c.tradingResult.account.diff !== 0 ||
        c.balanceFix.account.diff !== 0,
    )

  const handleLoad = () => {
    if (!dateFrom || !dateTo) return
    loadRdcCheck({
      dateFrom: dateFrom.format('YYYY-MM-DD'),
      dateTo: dateTo.format('YYYY-MM-DD'),
    })
  }

  const collapseItems = data.map((day) => {
    const warn = hasDiff(day.currencies)
    const rows = buildRows(day.currencies, currencyFilter)

    return {
      key: day.date,
      label: (
        <Space>
          <span>{day.date}</span>
          {warn && (
            <Badge
              count={<WarningOutlined style={{ color: '#faad14' }} />}
              style={{ backgroundColor: 'transparent' }}
            />
          )}
        </Space>
      ),
      children: (
        <Table<TableRow>
          columns={columns}
          dataSource={rows}
          rowKey="key"
          size="small"
          pagination={false}
          bordered
          scroll={{ x: 700 }}
        />
      ),
    }
  })

  return (
    <div>
      <Space style={{ marginBottom: 16 }} wrap>
        <DatePicker
          value={dateFrom}
          onChange={setDateFrom}
          placeholder="От"
          format="YYYY-MM-DD"
          allowClear={false}
        />
        <DatePicker
          value={dateTo}
          onChange={setDateTo}
          placeholder="До"
          format="YYYY-MM-DD"
          allowClear={false}
        />
        <Button type="primary" onClick={handleLoad} loading={loading} disabled={!dateFrom || !dateTo}>
          Загрузить
        </Button>
        {allCurrencies.length > 0 && (
          <Select
            mode="multiple"
            allowClear
            placeholder="Фильтр по валюте"
            value={currencyFilter}
            onChange={setCurrencyFilter}
            style={{ minWidth: 200 }}
            options={allCurrencies.map((c) => ({ label: c, value: c }))}
          />
        )}
      </Space>

      <Spin spinning={loading} tip="Запрос может занять некоторое время...">
        {data.length > 0 ? (
          <Collapse items={collapseItems} defaultActiveKey={data.map((d) => d.date)} />
        ) : (
          !loading && (
            <Text type="secondary">Выберите период и нажмите «Загрузить»</Text>
          )
        )}
      </Spin>
    </div>
  )
}
